/**
 * Copy for the one condition where every image a post asked for was delivered
 * but none of them is anchored to the business's own photo: the library photo
 * the agent picked could not be READ, so the image(s) were regenerated from the
 * prompt alone. `content.image_generation.reference_dropped` is the only signal
 * — the counts show no shortfall, because one image was asked for and one was
 * delivered.
 *
 * One sentence, three places. The post detail panel shows it as a notice
 * (`PostArtifactPanel`). The content-list row shows it in place of the
 * LLM-authored `user_summary` (`ArtifactRow`), because that summary is written
 * when the agent CALLS the creation tool — before generation runs — so on a
 * substitution it can still promise the photo ("anchored to the product photo
 * on file"). And the backend writes the same sentence into `user_summary` at
 * substitution time, so a corrected artifact and one the browser corrects on
 * read are indistinguishable.
 *
 * Keep in sync with `REFERENCE_DROPPED_USER_SUMMARY` /
 * `REFERENCE_DROPPED_USER_SUMMARY_CAROUSEL` in
 * `services/py/shared/models/artifact_content.py`.
 */
export const referenceDroppedNotice = (isCarousel: boolean): string =>
	isCarousel
		? "The photo chosen from your library couldn't be read, so these images were generated from the descriptions alone."
		: "The photo chosen from your library couldn't be read, so this image was generated from the description alone.";

type ReferenceDroppedContent = {
	image_urls?: string[];
	slide_prompts?: string[];
	image_generation?: {
		reference_dropped?: boolean;
		requested_count?: number;
		partial?: boolean;
	} | null;
};

/**
 * Whether a post is a multi-slide carousel, from the widest signal available.
 * `image_urls`/`slide_prompts` alone under-count a carousel still mid-generation,
 * so `requested_count` is consulted too — but only while generation is still
 * `partial`. Once generation has settled, a shrunk `image_urls`/`slide_prompts`
 * reflects a deliberate later edit (e.g. the customer removed a slide via the
 * filmstrip), and a stale `requested_count` left over from the original
 * generation must not override that.
 */
export const isMultiSlidePost = (content: ReferenceDroppedContent): boolean =>
	Math.max(
		content.image_urls?.length ?? 0,
		content.slide_prompts?.length ?? 0,
		content.image_generation?.partial
			? (content.image_generation.requested_count ?? 0)
			: 0,
	) > 1;

/**
 * Resolves the reference-dropped notice for an instagram post, or null if none
 * applies. Each caller supplies its own `suppressed` rule — e.g. the detail
 * panel suppresses this while a carousel is still partial, since the partial
 * banner already covers that case and wins the one notice slot; the
 * content-list row has no partial copy to defer to, so it never suppresses.
 */
export const resolveReferenceDroppedNotice = (
	content: ReferenceDroppedContent,
	suppressed: boolean,
): string | null => {
	if (suppressed || !content.image_generation?.reference_dropped) return null;
	return referenceDroppedNotice(isMultiSlidePost(content));
};
